import { useEffect, useRef, useState } from 'react';
import {
  DocumentEditorContainerComponent,
  Toolbar as DocEditorToolbar,
} from '@syncfusion/ej2-react-documenteditor';
import { apiCall, getDocxServiceUrl, getServerUrl } from '../api/client';
import { useAuthStore } from '../stores/authStore';
import ManageTemplatesDialog from './ManageTemplatesDialog';
import DictateButton, { DictateButtonHandle } from './DictateButton';
import HeaderView, { HeaderConfig, StudyFields, migrateHeaderConfig, DEFAULT_HEADER_CONFIG } from './HeaderView';
import { loadUserSettings, loadSettingsFull } from '../utils/userSettings';
import { notifyReportSigned } from '../plugins/runtime/host/reportBackHook';

/** Extract plain text from an SFDT (Syncfusion) content blob for sending
 *  back to runtime plugins. The vendor APIs (LIRA, Aidoc, etc.) take .txt
 *  not .docx; walking the SFDT tree gives us a clean linear text. */
function extractPlainTextForPlugins(content: string): string {
  try {
    const sfdt = JSON.parse(content);
    const parts: string[] = [];
    walk(sfdt);
    return parts.join('').trim();
    function walk(node: any) {
      if (!node || typeof node !== 'object') return;
      if (typeof node.text === 'string') parts.push(node.text);
      // Paragraph boundaries → newlines
      if (Array.isArray(node.blocks)) for (const b of node.blocks) { walk(b); parts.push('\n'); }
      if (Array.isArray(node.sections)) for (const s of node.sections) walk(s);
      if (Array.isArray(node.inlines)) for (const i of node.inlines) walk(i);
    }
  } catch {
    return content;
  }
}
import {
  installHeader, installHeaderPrependingBody, buildHeaderBlocks,
  stripHeaderFromSfdt, pasteHeaderAtDocumentStart, detectBakedHeaderEndIndex,
} from '../utils/insertHeader';
import { Macro, loadMacros, findMacroByTrigger, findMacroByVoice } from '../utils/macros';
import { listKeyImages, keyImageThumbUrl } from '../utils/keyImages';
import { broadcastReportChanged } from '../utils/worklistEvents';
import { readCachedHeaderConfig } from '../utils/headerConfigCache';
import { isAdminUsername } from '../utils/adminGate';
import {
  PageFormat, DEFAULT_PAGE_FORMAT,
  loadPageFormat, savePageFormat, applyPageFormatToCurrentSection,
} from '../utils/pageFormat';
import styles from './ReportEditor.module.css';

DocumentEditorContainerComponent.Inject(DocEditorToolbar);

export interface TemplateMeta {
  templateId: number;
  name: string;
  modality?: string;
  bodyPart?: string;
}

type Mode = 'report' | 'template';

interface Props {
  /** 'report' = bound to a studyId, 'template' = editing a reusable template */
  mode?: Mode;
  /** Required when mode='report' */
  studyId?: number;
  /** Required when mode='template'. 0 = new blank template (uses name/modality from template meta) */
  templateId?: number;
  /** Template metadata when mode='template' (name/modality/bodyPart editable from a separate form) */
  templateMeta?: TemplateMeta;
  /** Modality of the current study — used to filter templates list in report mode */
  modality?: string;
  /** Full study fields — used to build the report header grid inside the document */
  study?: StudyFields;
  /** Fill the entire parent (popout window) */
  fullScreen?: boolean;
  /** Panel-mode only; popout has the browser's own close button */
  onClose?: () => void;
  /** Label + handler for the back button rendered at the left of the header.
   *  When present, replaces the separate page-level banner (single-row layout). */
  backLabel?: string;
  onBack?: () => void;
}

const AUTOSAVE_INTERVAL_MS = 30_000;

/** When editing a template we don't know the study yet, so HeaderView is
 *  rendered with these placeholder tokens instead of real patient data.
 *  Users then see e.g. "Patient Name: {{patientName}}" - a clear visual
 *  cue that the header will be filled at report time. */
const PLACEHOLDER_STUDY: StudyFields = {
  patientName: '{{patientName}}',
  patientId: '{{patientId}}',
  patientDob: '{{patientDob}}',
  patientSex: '{{patientSex}}',
  patientAge: '{{patientAge}}',
  accessionNumber: '{{accessionNumber}}',
  studyDate: '{{studyDate}}',
  studyDescription: '{{studyDescription}}',
  modality: '{{modality}}',
  referringPhysician: '{{referringPhysician}}',
  institution: '{{institution}}',
};

export default function ReportEditor({
  mode = 'report', studyId, templateId, templateMeta,
  modality, study, fullScreen = false, onClose, backLabel, onBack,
}: Props) {
  const editorRef = useRef<DocumentEditorContainerComponent>(null);
  const dictateRef = useRef<DictateButtonHandle>(null);
  const username = useAuthStore((s) => s.username);
  const [templates, setTemplates] = useState<TemplateMeta[]>([]);
  const [status, setStatus] = useState<'draft' | 'signed' | 'new'>('new');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [showManageTpl, setShowManageTpl] = useState(false);
  const [currentTemplateId, setCurrentTemplateId] = useState<number>(templateId ?? 0);
  const [tplDropdownValue, setTplDropdownValue] = useState('');

  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const signedRef = useRef(false);
  const autosaveTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const loadingInitialRef = useRef(true);

  const isTemplateMode = mode === 'template';
  const headerConfigRef = useRef<HeaderConfig | null>(null);
  // Duplicated as state so the HeaderView rendered above the editor
  // re-renders once the config is loaded (refs don't trigger renders).
  const [headerConfig, setHeaderConfig] = useState<HeaderConfig | null>(null);
  // Tracks WHERE the current header config came from so we can surface it
  // in the UI. When 'default' shows up on a user who's saved a header, it
  // means the server-side settings.reportHeader returned empty AND the
  // localStorage backup is missing - both are diagnosable failure modes.
  const [headerConfigSource, setHeaderConfigSource] = useState<'server' | 'local' | 'default' | null>(null);
  // User's saved report page format (page size, margins, header/footer
  // distance). Loaded once in the init useEffect and used in three places:
  //   - installHeader / applyTemplate  -> baked into the SFDT the editor opens
  //   - onEditorCreated                 -> setDefaultSectionFormat so subsequent
  //                                        openBlank() calls default to it
  //   - Save-as-default button          -> writes selection.sectionFormat back
  const pageFormatRef = useRef<PageFormat>(DEFAULT_PAGE_FORMAT);
  const macrosRef = useRef<Macro[]>([]);
  // Rolling buffer of recently-typed characters, reset on whitespace / cursor move.
  // Used to detect \trigger completion without having to introspect Syncfusion's text.
  const typeBufRef = useRef('');

  function broadcastReportStatus(s: 'none' | 'draft' | 'signed') {
    if (isTemplateMode || studyId == null) return;
    try {
      localStorage.setItem(`edgeweb-report-status-${studyId}`, `${s}|${Date.now()}`);
    } catch { /* private mode, quota, etc. */ }
    // Same-tab: fire a CustomEvent so the worklist refreshes even when it's
    // in the same SPA tab as the report editor (storage events don't fire
    // in the writing tab).
    broadcastReportChanged();
  }

  /** Fresh header for a new blank report (replaces document). */
  function insertHeaderIntoEditor(delayMs = 250) {
    if (isTemplateMode) return;
    setTimeout(() => {
      const cfg = headerConfigRef.current || DEFAULT_HEADER_CONFIG;
      installHeader(editorRef.current, cfg, study || {}, pageFormatRef.current);
    }, delayMs);
  }

  /** Prepend header to existing editor content (e.g., after loading a template). */
  function prependHeaderToCurrentDoc(delayMs = 250) {
    if (isTemplateMode) return;
    setTimeout(() => {
      const cfg = headerConfigRef.current || DEFAULT_HEADER_CONFIG;
      installHeaderPrependingBody(editorRef.current, cfg, study || {});
    }, delayMs);
  }

  // Load templates list, header config, and any existing document — all awaited
  // in the correct order so the header config is guaranteed present before we
  // decide whether to render the header into a fresh doc.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      // Always load the header config - it's needed for the read-only
      // HeaderView preview rendered above the editor in BOTH modes
      // (report shows resolved study data; template shows placeholder tokens).
      //
      // force=true bypasses the module-level settings cache. That cache is
      // populated the first time anyone calls loadSettingsFull() in this
      // session - if a stale/empty snapshot got in early (e.g. before the
      // user saved their header design in a prior tab) it can serve wrong
      // data here. Costing an extra HTTP round-trip on every report open is
      // acceptable; a wrong header on every new report is not.
      const settingsFull = await loadSettingsFull(true);
      const settings = settingsFull.values;
      if (cancelled) return;
      // Server value wins; local backup is used only when the server side
      // is missing or unparseable. HeaderDesigner writes both on save.
      let source: 'server' | 'local' | 'default' = 'default';
      let raw: string | undefined = settings.reportHeader;
      const localRaw = readCachedHeaderConfig();
      if (!raw) {
        if (localRaw) { raw = localRaw; source = 'local'; }
      } else {
        source = 'server';
      }
      if (raw) {
        try {
          const cfg = migrateHeaderConfig(JSON.parse(raw));
          headerConfigRef.current = cfg;
          setHeaderConfig(cfg);
        } catch (err) {
          console.warn('[ReportEditor] reportHeader parse failed, using default', err);
          headerConfigRef.current = DEFAULT_HEADER_CONFIG;
          setHeaderConfig(DEFAULT_HEADER_CONFIG);
          source = 'default';
        }
      } else {
        headerConfigRef.current = DEFAULT_HEADER_CONFIG;
        setHeaderConfig(DEFAULT_HEADER_CONFIG);
      }
      setHeaderConfigSource(source);
      // Fat log so we can pinpoint which source got picked and why the
      // others were rejected. Grep the browser console for this line
      // when a wrong header shows up.
      console.info('[ReportEditor] headerConfig loaded', {
        source,
        serverHasKey: !!settings.reportHeader,
        serverLen: settings.reportHeader?.length ?? 0,
        serverScope: settingsFull.scope.reportHeader || null,
        localHasKey: !!localRaw,
        localLen: localRaw?.length ?? 0,
      });

      // Load the user's saved report Page Setup (or fall back to US Letter
      // with 1" margins). Cached in a ref so builders can inline it into
      // fresh SFDT without another async hop. Applied to the editor via
      // whenEditorReady so we don't race the container's async mount.
      try {
        const pf = await loadPageFormat();
        if (!cancelled) {
          pageFormatRef.current = pf.format;
          // Register as the editor's default so any subsequent openBlank()
          // (Delete Draft, template mode with no currentTemplateId, etc.)
          // uses these values instead of Syncfusion's built-in Letter.
          // whenEditorReady polls documentEditor.viewer until non-null -
          // safer than the JSX `created` prop which caused an EJ2 internal
          // Object.defineProperty crash on some code paths.
          whenEditorReady(() => {
            try {
              const de: any = editorRef.current?.documentEditor;
              de?.setDefaultSectionFormat?.(pf.format);
            } catch (err) {
              console.warn('setDefaultSectionFormat failed', err);
            }
          });
        }
      } catch (err) {
        console.warn('[ReportEditor] pageFormat load failed, using default', err);
      }

      if (!isTemplateMode) {
        // Fire-and-forget: template dropdown can populate independently
        apiCall<TemplateMeta>('templates.list', { modality: modality || '' }).then((res) => {
          if (!cancelled && res.s === 'ok') setTemplates(res.data || []);
        });
        // Load macros for text-trigger and voice-alias expansion
        loadMacros().then((r) => {
          if (!cancelled) macrosRef.current = r.macros;
        });
      }

      if (isTemplateMode) {
        // Templates are BODY ONLY - no letterhead, no patient grid. The
        // author writes only the reusable prose; the header comes in
        // at report time from the current HeaderConfig + real study.
        if (!currentTemplateId) {
          // New template - editor stays blank ready for typing.
          setTimeout(() => { loadingInitialRef.current = false; dirtyRef.current = false; }, 300);
          return;
        }
        const res = await apiCall<{ content: string; contentType?: string }>(
          'templates.get', { templateId: currentTemplateId }
        );
        if (cancelled) return;
        if (res.s === 'ok' && res.data?.[0]?.content) {
          // Strip any legacy header baked into old templates so only the
          // body reaches the editor.
          const raw = res.data[0].content;
          const type = res.data[0].contentType || 'sfdt';
          loadBase64Content(maybeStripHeaderInBase64(raw, type), type);
          // Overwrite the loaded doc's section format with the user's
          // current Page Setup default. Deferred so the editor has time
          // to parse the SFDT before we mutate its current section.
          setTimeout(() => {
            applyPageFormatToCurrentSection(editorRef.current, pageFormatRef.current);
            // Belt-and-braces re-clear: the sectionFormat mutation fires
            // Syncfusion's contentChange event which is normally caught by
            // the loadingInitialRef guard - this catches the race where the
            // editor mounted slow and the guard has already been cleared.
            setTimeout(() => { dirtyRef.current = false; }, 50);
          }, 300);
        }
        setTimeout(() => { loadingInitialRef.current = false; dirtyRef.current = false; }, 500);
        return;
      }

      if (studyId == null) return;

      const res = await apiCall<{ content: string; status: string; contentType?: string }>(
        'reports.get', { studyId }
      );
      if (cancelled) return;

      const row = (res.s === 'ok' && res.data?.[0]) ? res.data[0] : null;
      const savedStatus = (row?.status as 'draft' | 'signed' | 'none') || 'none';

      if (row && savedStatus !== 'none' && row.content) {
        setStatus(savedStatus as 'draft' | 'signed');
        signedRef.current = savedStatus === 'signed';
        // Load the saved SFDT AS-IS. Reports are saved with the header
        // baked in (no stripping), so a straight open() puts the user
        // back exactly where they left off - body, header, everything.
        // Doing header stripping + re-prepending was fragile: the
        // styleName sentinel we relied on to find the header boundary
        // gets dropped by Syncfusion on round-trip (custom style names
        // aren't preserved unless registered in document.styles), so
        // the strip step sometimes returned the whole document and the
        // re-prepend step then produced malformed SFDT that rendered blank.
        // Tradeoff: changes made in Header Designer after a report is
        // saved no longer retroactively apply to that report. That's fine
        // - a signed/draft report SHOULD stay visually stable across
        // header-template edits.
        const type = row.contentType || 'sfdt';
        if (type === 'sfdt') {
          whenEditorReady(() => {
            loadBase64Content(row.content, 'sfdt');
            // Overwrite loaded doc's section format with the user's
            // saved default so an old draft/signed picks up the current
            // page geometry when reopened. Content is untouched - only
            // page size, margins, and layout are updated. See
            // help.syncfusion.com/document-processing/word/word-processor/
            // react/section-format for the selection.sectionFormat API.
            setTimeout(() => {
              applyPageFormatToCurrentSection(editorRef.current, pageFormatRef.current);
              // Re-silence dirty in case the mutation raced the guard.
              setTimeout(() => { dirtyRef.current = false; }, 50);
            }, 300);
            setTimeout(() => { dirtyRef.current = false; }, 250);
          });
        } else {
          // Non-SFDT content (docx blob) - open as blob and skip header
          // prepend (blob path can't be recomposed in JS).
          loadBase64Content(row.content, type);
          whenEditorReady(() => {
            setTimeout(() => {
              applyPageFormatToCurrentSection(editorRef.current, pageFormatRef.current);
              setTimeout(() => { dirtyRef.current = false; }, 50);
            }, 300);
            setTimeout(() => { dirtyRef.current = false; }, 250);
          });
        }
      } else {
        // Brand-new report - insert header-only skeleton so the editor
        // shows the letterhead + patient grid the moment it opens.
        whenEditorReady(() => {
          installHeader(editorRef.current, headerConfigRef.current, study || {}, pageFormatRef.current);
          setTimeout(() => { dirtyRef.current = false; }, 250);
        });
      }
      setTimeout(() => { loadingInitialRef.current = false; dirtyRef.current = false; }, 700);
    })();

    return () => { cancelled = true; };
  }, [isTemplateMode, studyId, modality, currentTemplateId]);

  // Report-mode only: header edit-guard. If the user touches the header
  // paragraphs (types into them, deletes them, presses Enter inside a
  // grid cell), the sentinel-paragraph position changes. On the next
  // debounced tick we re-inject a freshly-built header on top of what
  // survives, giving the visceral "I can't edit this" feel without the
  // complexity of Syncfusion's RestrictedEditing API. Template mode
  // has no header so this is a no-op there.
  const rebuildingHeaderRef = useRef(false);
  const headerGuardTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Subscribe to editor's contentChange
  useEffect(() => {
    const editor = editorRef.current?.documentEditor;
    if (!editor) return;
    const onChange = () => {
      if (loadingInitialRef.current) return;
      if (signedRef.current) return;
      if (rebuildingHeaderRef.current) return;
      dirtyRef.current = true;
      // Header edit-guard disabled: it depended on a styleName sentinel
      // (paragraphFormat.styleName='EdgeHeaderEnd') to detect whether
      // the header had drifted. Syncfusion drops unknown style names on
      // round-trip - so the guard fires on every keystroke, re-injecting
      // a fresh header and stacking duplicates or wiping content. Users
      // will just have to leave the letterhead cells alone; nothing
      // enforces that yet.
    };
    editor.contentChange = onChange;
    return () => {
      if (headerGuardTimeoutRef.current) clearTimeout(headerGuardTimeoutRef.current);
      // Syncfusion's setter internally calls off() which walks a listener map;
      // during unmount that map may already be null, which crashes and breaks
      // React's rendering tree. Swallow — the editor is being destroyed anyway.
      try {
        if (editor.contentChange === onChange) editor.contentChange = undefined as any;
      } catch { /* editor already torn down */ }
    };
  }, [editorRef.current?.documentEditor, isTemplateMode, study]);

  // Periodic auto-save (silent)
  useEffect(() => {
    autosaveTimer.current = setInterval(() => {
      if (dirtyRef.current && !savingRef.current && !signedRef.current) {
        void autoSave();
      }
    }, AUTOSAVE_INTERVAL_MS);
    return () => { if (autosaveTimer.current) clearInterval(autosaveTimer.current); };
  }, []);

  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (!dirtyRef.current || signedRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [studyId, currentTemplateId]);

  // Expose a guard so surrounding page shells (ReportPage/TemplatePage) can block
  // their own "Back" buttons and other in-app navigation. Returns true if it's safe
  // to proceed (clean OR user confirmed discard).
  useEffect(() => {
    const key = '__reportEditorCloseGuard';
    (window as any)[key] = () => {
      if (!dirtyRef.current || signedRef.current) return true;
      return confirm('You have unsaved changes. Discard them and continue?');
    };
    return () => { delete (window as any)[key]; };
  }, []);

  // Keyboard shortcuts + \trigger macro expansion.
  //
  // Syncfusion's own keydown handler listens on its editable div and intercepts
  // Ctrl+S to save the doc as an SFDT file. It fires a `keyDown` event first
  // with an `isHandled` flag; setting it true tells Syncfusion to skip its
  // built-in handling. That's the supported way to override.
  useEffect(() => {
    if (isTemplateMode) return;
    const editor: any = editorRef.current?.documentEditor;
    if (!editor) return;

    function tryExpandMacro(): boolean {
      const buf = typeBufRef.current;
      console.log('[macro] tryExpand buf=', JSON.stringify(buf), 'macroCount=', macrosRef.current.length);
      if (!buf || !buf.startsWith('\\')) return false;
      const macro = findMacroByTrigger(macrosRef.current, buf);
      if (!macro) { console.log('[macro] no match for', JSON.stringify(buf)); return false; }
      console.log('[macro] MATCH', macro.trigger);
      try {
        for (let i = 0; i < buf.length; i++) editor.editor.handleBackKey();
        editor.editor.insertText(macro.expansion);
        typeBufRef.current = '';
        dirtyRef.current = true;
        showMessage(`Macro: \\${macro.trigger}`);
        return true;
      } catch (err) { console.error('[macro] expand threw', err); return false; }
    }

    // Syncfusion's event fires for navigation keys only; its one job here is
    // to catch Ctrl+S / Ctrl+Shift+S and block Syncfusion's own "download SFDT"
    // shortcut via isHandled=true. Buffer maintenance lives in the DOM listener
    // below — doing it in both places double-counted every keystroke.
    function onSyncfusionKeyDown(args: any) {
      const ev: KeyboardEvent = args?.event;
      if (!ev) return;
      const ctrl = ev.ctrlKey || ev.metaKey;

      if (ctrl && ev.shiftKey && !ev.altKey && (ev.key === 'S' || ev.key === 's')) {
        ev.preventDefault();
        args.isHandled = true;
        if (status !== 'signed' && !saving) void saveReport(true);
        return;
      }
      if (ctrl && !ev.shiftKey && !ev.altKey && (ev.key === 's' || ev.key === 'S')) {
        ev.preventDefault();
        args.isHandled = true;
        if (status !== 'signed' && !saving) void saveReport(false);
        return;
      }
    }

    function onWindowKeyDown(e: KeyboardEvent) {
      const ctrl = e.ctrlKey || e.metaKey;

      // Ctrl+Shift+S / Ctrl+S — also work outside the editable area
      if (ctrl && e.shiftKey && !e.altKey && (e.key === 'S' || e.key === 's')) {
        e.preventDefault();
        if (status !== 'signed' && !saving) void saveReport(true);
        return;
      }
      if (ctrl && !e.shiftKey && !e.altKey && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        if (status !== 'signed' && !saving) void saveReport(false);
        return;
      }
      // F4 — toggle dictation (Syncfusion doesn't bind F4)
      if (e.key === 'F4' && !ctrl && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        if (status !== 'signed') dictateRef.current?.toggle();
        return;
      }
      // Esc — back to viewer (only if the user is on the report page AND not typing in an input)
      if (e.key === 'Escape' && !ctrl && !e.altKey && !e.shiftKey) {
        const tgt = e.target as HTMLElement | null;
        const isInput = tgt && (tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA' || tgt.isContentEditable);
        if (!isInput && onBack) {
          e.preventDefault();
          if (!dirtyRef.current || signedRef.current || confirmDiscardIfDirty('leave')) onBack();
        }
      }
    }

    // Syncfusion hosts its contenteditable div inside an iframe (see
    // initIframeContent in viewer.js). Key events fired inside that iframe do
    // NOT bubble to the parent document — so we must attach our listener to
    // the IFRAME's document, not window/document of the outer page.
    const iframeDoc: Document | null =
      editor.documentHelper?.iframe?.contentDocument || null;

    function onDomKeyDown(e: KeyboardEvent) {
      // Dedupe: if the same KeyboardEvent fires on multiple listeners (outer
      // document + iframe document + editableDiv), only process the first.
      if ((e as any).__macroSeen) return;
      (e as any).__macroSeen = true;

      const ctrl = e.ctrlKey || e.metaKey;
      console.log('[macro] keyDown key=', JSON.stringify(e.key), 'buf=', JSON.stringify(typeBufRef.current));

      // Macro completion on space / Tab / Enter (only when the buffer actually
      // looks like a trigger — otherwise let Syncfusion process normally).
      if (!ctrl && !e.altKey && (e.key === ' ' || e.key === 'Tab' || e.key === 'Enter')) {
        if (tryExpandMacro()) {
          e.preventDefault();
          e.stopPropagation();
        } else {
          typeBufRef.current = '';
        }
        return;
      }

      // Buffer maintenance
      if (e.key === 'Backspace') {
        typeBufRef.current = typeBufRef.current.slice(0, -1);
      } else if (e.key.length === 1 && !ctrl && !e.altKey) {
        typeBufRef.current += e.key;
        if (typeBufRef.current.length > 40) {
          typeBufRef.current = typeBufRef.current.slice(-40);
        }
      } else if (
        e.key === 'ArrowLeft' || e.key === 'ArrowRight' ||
        e.key === 'ArrowUp'   || e.key === 'ArrowDown'  ||
        e.key === 'Home'      || e.key === 'End'        ||
        e.key === 'PageUp'    || e.key === 'PageDown'   ||
        e.key === 'Delete'    || e.key === 'Escape'
      ) {
        typeBufRef.current = '';
      }
    }

    // Input-event path (works even when keydown gets swallowed).
    // Syncfusion's editableDiv fires `input` with e.data = the inserted char,
    // or inputType telling us about deletes / paragraph breaks. We accumulate
    // to the buffer from here instead of from keydown. keydown is kept for
    // Tab/Enter/Space handling and for Ctrl+S (via Syncfusion's keyDown event).
    function onInput(e: Event) {
      const ie = e as InputEvent;
      console.log('[macro] input inputType=', ie.inputType, 'data=', JSON.stringify(ie.data));
      if (!ie.inputType) return;
      if (ie.inputType === 'insertText' && ie.data) {
        if (/^\s$/.test(ie.data)) {
          // Space/tab/newline — try to expand
          if (tryExpandMacro()) {
            // insertText already inserted the space — delete it
            try { editor.editor.handleBackKey(); } catch {}
          } else {
            typeBufRef.current = '';
          }
          return;
        }
        typeBufRef.current += ie.data;
        if (typeBufRef.current.length > 40) {
          typeBufRef.current = typeBufRef.current.slice(-40);
        }
      } else if (ie.inputType === 'deleteContentBackward' || ie.inputType === 'deleteContentForward') {
        typeBufRef.current = typeBufRef.current.slice(0, -1);
      } else if (
        ie.inputType === 'insertParagraph' || ie.inputType === 'insertLineBreak'
      ) {
        if (!tryExpandMacro()) typeBufRef.current = '';
      }
    }

    // Syncfusion calls iframe.contentDocument.open() on every report load,
    // which wipes any listeners attached to that document — BUT the
    // contentDocument reference stays the same, so reference equality
    // (doc !== attachedIframeDoc) can't detect the wipe. Workaround: always
    // call addEventListener every poll tick. The spec says adding the same
    // (type, function, capture) is a no-op when already registered, so this
    // is both safe and automatically re-attaches after a document.open reset.
    let lastEditable: HTMLElement | null = null;
    function reattach() {
      const doc: Document | null =
        editor.documentHelper?.iframe?.contentDocument || null;
      const ed: HTMLElement | null = editor.documentHelper?.editableDiv || null;
      if (doc) {
        try { doc.addEventListener('keydown', onDomKeyDown, true); } catch {}
      }
      if (ed) {
        try { ed.addEventListener('input', onInput); } catch {}
        try { ed.addEventListener('beforeinput', onInput as any); } catch {}
      }
      lastEditable = ed;
    }

    editor.keyDown = onSyncfusionKeyDown;
    document.addEventListener('keydown', onDomKeyDown, true);
    window.addEventListener('keydown', onWindowKeyDown, true);
    reattach();
    const pollTimer = setInterval(reattach, 500);

    return () => {
      try { if (editor.keyDown === onSyncfusionKeyDown) editor.keyDown = undefined; } catch {}
      document.removeEventListener('keydown', onDomKeyDown, true);
      window.removeEventListener('keydown', onWindowKeyDown, true);
      clearInterval(pollTimer);
      // Best-effort cleanup on the currently-known iframe doc / editableDiv.
      // Syncfusion is about to destroy the editor anyway, so orphan listeners
      // get garbage-collected with the elements.
      try {
        const doc = editor.documentHelper?.iframe?.contentDocument;
        if (doc) doc.removeEventListener('keydown', onDomKeyDown, true);
      } catch {}
      try {
        if (lastEditable) {
          lastEditable.removeEventListener('input', onInput);
          lastEditable.removeEventListener('beforeinput', onInput as any);
        }
      } catch {}
    };
  }, [isTemplateMode, status, saving, onBack, editorRef.current?.documentEditor]);

  // Paste is now handled by the Syncfusion DocumentEditor server (see serviceUrl
  // on the container). The previous custom event interceptor was a workaround
  // for when no server was available; it's removed since the real service
  // handles docx/html/rtf clipboard content with full fidelity.

  /** Check dirty state before a destructive action (load template / edit another template).
   *  Returns true if the caller should proceed. */
  function confirmDiscardIfDirty(action: string): boolean {
    if (!dirtyRef.current || signedRef.current) return true;
    return confirm(`You have unsaved changes. Discard them and ${action}?`);
  }

  /** Poll for the Syncfusion editor to be mounted and ready, then run
   *  `fn`. Beats a fixed setTimeout that occasionally fires before the
   *  component finishes booting - symptoms of that race were headers
   *  silently failing to appear on some load paths.
   *
   *  Retries every 100 ms for up to 3 seconds. Uses `documentEditor.viewer`
   *  as the readiness signal since it's set only after full initialisation. */
  function whenEditorReady(fn: () => void) {
    let tries = 0;
    const tryRun = () => {
      const ed: any = editorRef.current?.documentEditor;
      const ready = !!ed?.viewer;
      if (ready) { fn(); return; }
      if (tries++ > 30) { fn(); return; }   // give up after 3s - just try
      setTimeout(tryRun, 100);
    };
    tryRun();
  }

  function loadBase64Content(base64: string, contentType: string) {
    if (!base64) return;
    try {
      const editor = editorRef.current?.documentEditor;
      if (!editor) return;
      loadingInitialRef.current = true;

      if (contentType === 'sfdt') {
        const sfdt = new TextDecoder('utf-8').decode(base64ToBytes(base64));
        editor.open(sfdt);
        return;
      }

      const bytes = base64ToBytes(base64);
      const blob = new Blob([bytes.buffer as ArrayBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      editor.open(blob);
    } catch (err) {
      console.error('Failed to load document content:', err);
    }
  }

  async function applyTemplate(tplId: number) {
    if (!confirmDiscardIfDirty('load this template')) {
      setMessage('Template load cancelled.');
      return;
    }
    const res = await apiCall<{ content: string; contentType?: string }>('templates.get', { templateId: tplId });
    if (res.s !== 'ok') {
      setMessage('Load failed: ' + (res.error || 'server error'));
      return;
    }
    if (!res.data?.[0]?.content) {
      setMessage('Template is empty.');
      return;
    }

    const raw = res.data[0].content;
    const type = res.data[0].contentType || 'sfdt';
    const editor: any = editorRef.current?.documentEditor;
    if (!editor) {
      setMessage('Editor not ready.');
      return;
    }

    // Non-SFDT (docx blob): can't paste as SFDT, fall back to the older path.
    if (type !== 'sfdt') {
      loadBase64Content(raw, type);
      whenEditorReady(() => {
        installHeaderPrependingBody(editorRef.current, headerConfigRef.current, study || {});
      });
      showMessage('Template loaded');
      return;
    }

    // Iron-clad two-step: (1) openAsync the template body (awaits parse +
    // layout, returns real Promise), (2) moveToDocumentStart, (3) paste the
    // header SFDT at that position. paste() uses Syncfusion's own clipboard
    // code path which handles style dedup, list-id remap, and image merging
    // - all the merge concerns that hand-composed SFDT gets wrong.
    // See: syncfusion-research.md in the repo root.
    loadingInitialRef.current = true;
    try {
      const stripped = maybeStripHeaderInBase64(raw, 'sfdt');
      let bodySfdt = new TextDecoder('utf-8').decode(base64ToBytes(stripped));

      // Legacy templates got their header baked in at save time because
      // stripHeaderFromSfdt relied on a paragraphFormat.styleName sentinel
      // that Syncfusion silently drops on round-trip (custom style names
      // don't survive unless registered in document.styles[]). If the
      // template body starts with a header-shaped prefix (image / centered
      // paragraphs / patient-info table), strip it now before we paste our
      // own header on top - otherwise the report ends up with two stacked
      // headers, which is exactly the bug the user just reported.
      try {
        const bodyDoc = JSON.parse(bodySfdt);
        const rawBlocks = bodyDoc?.sections?.[0]?.blocks || [];
        const stripEnd = detectBakedHeaderEndIndex(rawBlocks);
        if (stripEnd > 0) {
          bodyDoc.sections[0].blocks = rawBlocks.slice(stripEnd);
          bodySfdt = JSON.stringify(bodyDoc);
          console.info(`[applyTemplate] stripped ${stripEnd} baked header blocks from template body`);
        }
      } catch (err) {
        console.warn('[applyTemplate] baked-header strip failed; loading body as-is', err);
      }

      await editor.openAsync(bodySfdt);
      pasteHeaderAtDocumentStart(editorRef.current, headerConfigRef.current, study || {}, pageFormatRef.current);

      // Overwrite the template body's section format with the user's saved
      // page setup. Syncfusion's paste() only inserts blocks; it does NOT
      // touch section format - so without this the report would come up
      // with whatever page size the template author happened to save with,
      // not the current user's preferred size. See how-to/set-default-
      // format-in-document-editor - selection.sectionFormat is the write path.
      try {
        const sf: any = editor.selection?.sectionFormat;
        const pf = pageFormatRef.current;
        if (sf && pf) {
          // Paper tab
          sf.pageWidth  = pf.pageWidth;
          sf.pageHeight = pf.pageHeight;
          // Margins tab
          sf.leftMargin    = pf.leftMargin;
          sf.rightMargin   = pf.rightMargin;
          sf.topMargin     = pf.topMargin;
          sf.bottomMargin  = pf.bottomMargin;
          // Layout tab
          sf.headerDistance          = pf.headerDistance;
          sf.footerDistance          = pf.footerDistance;
          sf.differentFirstPage      = pf.differentFirstPage;
          sf.differentOddAndEvenPages = pf.differentOddAndEvenPages;
          sf.numberOfColumns         = pf.numberOfColumns;
          sf.equalWidth              = pf.equalWidth;
          sf.lineBetweenColumns      = pf.lineBetweenColumns;
        }
      } catch (err) {
        console.warn('applyTemplate: applying user pageFormat failed', err);
      }

      // Clear undo history so Ctrl+Z can't roll back the programmatic header
      // insert into the user's face. Templates arrive with a clean history.
      try { editor.editorHistory?.clearHistory?.(); } catch { /* not critical */ }

      // Move caret to the end so the user starts typing after the body,
      // not inside the header we just pasted.
      try { editor.selection?.moveToDocumentEnd(); } catch { /* ignore */ }

      showMessage('Template loaded');
    } catch (err) {
      console.error('applyTemplate: openAsync/paste failed', err);
      setMessage('Load failed: could not apply template.');
    } finally {
      loadingInitialRef.current = false;
      // A freshly-loaded template is unsaved-in-progress -> mark dirty so
      // autosave/before-unload guards kick in.
      dirtyRef.current = true;
    }
  }

  /**
   * Serialize the editor to a base64 SFDT payload.
   * @param stripHeader If true, remove the resolved header (up to the
   *   `EdgeHeaderEnd` sentinel) before encoding. Use this whenever the
   *   destination is a template - a report saved as a template would
   *   otherwise carry the current patient's name / ID baked into the
   *   header, and that would appear in every future use of the template.
   */
  async function saveContentAsBase64(opts?: { stripHeader?: boolean }): Promise<string | null> {
    const editor = editorRef.current?.documentEditor;
    if (!editor) return null;
    let sfdt: string = editor.serialize();
    if (opts?.stripHeader) sfdt = stripHeaderFromSfdt(sfdt);
    return bytesToBase64(new TextEncoder().encode(sfdt));
  }

  function base64ToBytes(b64: string): Uint8Array {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  }

  function bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  }

  /**
   * Print the report the way the user sees it on screen: header at the
   * top (from HeaderView above the editor), then the body. Since the
   * editor's SFDT is body-only, we temporarily open a combined
   * `[header blocks] + [body blocks]` SFDT, fire editor.print(), then
   * restore the body-only content. Brief flicker, but Syncfusion's
   * built-in print already knows how to lay out an SFDT so we get
   * consistent output across HTML print and Save-as-PDF.
   */
  function printWithHeader() {
    const ed: any = editorRef.current?.documentEditor;
    if (!ed) return;
    const cfg = headerConfigRef.current;
    const studyForHeader = isTemplateMode ? PLACEHOLDER_STUDY : (study || {});

    if (!cfg) {
      try { ed.print(); } catch (err) { console.error('Print failed', err); }
      return;
    }

    let bodySfdt: string;
    try {
      bodySfdt = ed.serialize();
    } catch (err) {
      console.error('printWithHeader: serialize failed', err);
      try { ed.print(); } catch { /* last-ditch */ }
      return;
    }

    let combined: string;
    try {
      const bodyDoc = JSON.parse(bodySfdt);
      const headerBlocks = buildHeaderBlocks(cfg, studyForHeader);
      if (!bodyDoc.sections) bodyDoc.sections = [{ sectionFormat: {}, blocks: [] }];
      if (!bodyDoc.sections[0]) bodyDoc.sections[0] = { sectionFormat: {}, blocks: [] };
      bodyDoc.sections[0].blocks = [...headerBlocks, ...(bodyDoc.sections[0].blocks || [])];
      combined = JSON.stringify(bodyDoc);
    } catch (err) {
      console.error('printWithHeader: combine failed', err);
      try { ed.print(); } catch { /* last-ditch */ }
      return;
    }

    try {
      ed.open(combined);
    } catch (err) {
      console.error('printWithHeader: open combined failed', err);
      try { ed.print(); } catch { /* last-ditch */ }
      return;
    }

    // Give Syncfusion a tick to lay out the combined doc before opening
    // the print dialog. print() is synchronous once the dialog is up so
    // we can restore body-only content in a finally afterwards.
    setTimeout(() => {
      try { ed.print(); } catch (err) { console.error('Print failed', err); }
      // Restore body-only content regardless of how print resolved.
      try { ed.open(bodySfdt); } catch (err) { console.error('printWithHeader: restore failed', err); }
      // Restoring counts as a doc change - don't propagate that as dirty.
      dirtyRef.current = false;
    }, 50);
  }

  /**
   * High-quality print via the dcx-service PDF export path.
   *
   * Flow:
   *   1. Serialize the current editor to SFDT.
   *   2. POST it to dcx-service /api/documenteditor/ExportPdf.
   *   3. Receive a PDF blob back (vector, printer-native).
   *   4. Open it as a blob: URL in a new tab; the browser's PDF viewer
   *      renders + prints as vector — matches MS Word print quality.
   *
   * Why not editor.print()? Syncfusion's DocumentEditor renders each page
   * onto a canvas at screen DPI, converts it to PNG, and prints the PNG.
   * That's fuzzy compared to MS Word. printDevicePixelRatio=3 helps the
   * Quick print path but is still raster. This path is the only way to
   * get true vector output. Syncfusion themselves recommend this exact
   * approach: forum 152983 ("we have to achieve this in server-side with
   * the help of DOCIO"), forum 163880.
   *
   * Requires: dcx-service /ExportPdf endpoint (see EJ2APIServices sample
   * + Syncfusion.DocIORenderer.NET). Falls back to canvas print with a
   * user-visible message if the endpoint is missing or errors.
   */
  /**
   * Capture the CURRENT editor's section format (whatever the user set in
   * the Page Setup dialog moments ago) and persist it as the user's
   * default page format. Applied via setDefaultSectionFormat AND baked
   * into insertHeader / applyTemplate on every future report open.
   *
   * Reads every field the Page Setup dialog can write - Margins tab,
   * Paper tab, Layout tab - so a saved default round-trips exactly.
   * Confirms save via a toast; failures surface too.
   */
  async function saveCurrentPageAsDefault() {
    const ed: any = editorRef.current?.documentEditor;
    const sf: any = ed?.selection?.sectionFormat;
    if (!sf) {
      setMessage('Editor not ready.');
      return;
    }
    // Snapshot every field the dialog writes. Coerce with fallbacks so a
    // partial section (empty template body, e.g.) doesn't produce a
    // half-populated saved default.
    const pf: PageFormat = {
      pageWidth:  Number(sf.pageWidth)  || DEFAULT_PAGE_FORMAT.pageWidth,
      pageHeight: Number(sf.pageHeight) || DEFAULT_PAGE_FORMAT.pageHeight,
      leftMargin:   Number(sf.leftMargin)   ?? DEFAULT_PAGE_FORMAT.leftMargin,
      rightMargin:  Number(sf.rightMargin)  ?? DEFAULT_PAGE_FORMAT.rightMargin,
      topMargin:    Number(sf.topMargin)    ?? DEFAULT_PAGE_FORMAT.topMargin,
      bottomMargin: Number(sf.bottomMargin) ?? DEFAULT_PAGE_FORMAT.bottomMargin,
      headerDistance: Number(sf.headerDistance) ?? DEFAULT_PAGE_FORMAT.headerDistance,
      footerDistance: Number(sf.footerDistance) ?? DEFAULT_PAGE_FORMAT.footerDistance,
      differentFirstPage:       Boolean(sf.differentFirstPage),
      differentOddAndEvenPages: Boolean(sf.differentOddAndEvenPages),
      numberOfColumns: Number(sf.numberOfColumns) || 1,
      equalWidth:         sf.equalWidth !== false,   // default true
      lineBetweenColumns: Boolean(sf.lineBetweenColumns),
    };
    // Save at user scope so radiologists get personal preferences without
    // stepping on a clinic-wide default. Clinic admins can adjust the
    // clinic scope from Settings > Reports if the practice has a
    // standard.
    const ok = await savePageFormat(pf, 'user');
    if (ok) {
      pageFormatRef.current = pf;
      // Re-register with the editor so any subsequent openBlank in this
      // session already uses the new default.
      try { ed.setDefaultSectionFormat?.(pf); } catch { /* not critical */ }
      showMessage('Page Setup saved as your default');
    } else {
      setMessage('Save failed - could not persist Page Setup.');
    }
  }

  async function printAsPdf() {
    const ed: any = editorRef.current?.documentEditor;
    if (!ed) return;

    let sfdt: string;
    try {
      sfdt = ed.serialize();
    } catch (err) {
      console.error('printAsPdf: serialize failed', err);
      setMessage('Print failed: could not serialize the document.');
      return;
    }

    const url = getDocxServiceUrl().replace(/\/$/, '') + '/ExportPdf';
    setMessage('Preparing PDF for print...');
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: sfdt }),
      });
      if (!res.ok) {
        // 404 -> endpoint not deployed yet; fall back to canvas print
        // so the user isn't left without any print option at all.
        if (res.status === 404) {
          console.warn('printAsPdf: /ExportPdf not deployed; falling back to canvas print');
          setMessage('PDF export not available on this server. Using quick print instead.');
          try { ed.print(); } catch { /* ignore */ }
          setTimeout(() => setMessage(''), 2500);
          return;
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      // Open the PDF in a new tab. The user hits Ctrl+P in the browser's
      // PDF viewer to print. Revoking the blob URL after a delay so the
      // tab has time to load; browsers hold their own reference once loaded.
      const w = window.open(blobUrl, '_blank');
      if (!w) {
        setMessage('Popup blocked. Allow popups for this site and try again.');
        return;
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      setMessage('');
    } catch (err: any) {
      console.error('printAsPdf failed', err);
      setMessage('PDF export failed: ' + (err?.message || 'unknown'));
    }
  }

  /** Backward-compat: if a saved report/template still carries the header
   *  inline (baked into the SFDT with an EdgeHeaderEnd sentinel), strip
   *  it here so only the body reaches the editor. Post-refactor saves
   *  never contain a header, so this is a no-op for them. Non-SFDT
   *  content types (docx bytes, images) pass through unchanged. */
  function maybeStripHeaderInBase64(base64: string, contentType: string): string {
    if (contentType !== 'sfdt') return base64;
    try {
      const sfdt = new TextDecoder('utf-8').decode(base64ToBytes(base64));
      const stripped = stripHeaderFromSfdt(sfdt);
      if (stripped === sfdt) return base64;   // no sentinel found - unchanged
      return bytesToBase64(new TextEncoder().encode(stripped));
    } catch {
      return base64;
    }
  }

  async function autoSave() {
    savingRef.current = true;
    try {
      if (isTemplateMode) {
        if (!currentTemplateId) return; // no target yet — wait for explicit Save
        // Templates get a header-stripped payload so any residual baked-in
        // patient data (from a template created off a report before the
        // strip was in place) gets cleaned up on next autosave.
        const content = await saveContentAsBase64({ stripHeader: true });
        if (!content) return;
        const res = await apiCall('templates.save', {
          templateId: currentTemplateId,
          name: templateMeta?.name ?? '',
          modality: templateMeta?.modality ?? '',
          bodyPart: templateMeta?.bodyPart ?? '',
          content, contentType: 'sfdt',
        });
        if (res.s === 'ok') {
          dirtyRef.current = false;
          setLastSavedAt(new Date());
        }
        return;
      }

      if (studyId == null) return;
      // Save full document (header baked in). See saveReport for rationale
      // - the sentinel-based strip was unreliable and load-time header
      // re-prepending caused content to disappear on reopen.
      const content = await saveContentAsBase64();
      if (!content) return;
      const res = await apiCall('reports.save', { studyId, content, contentType: 'sfdt' });
      if (res.s === 'ok') {
        dirtyRef.current = false;
        setLastSavedAt(new Date());
        setStatus('draft');
        broadcastReportStatus('draft');
      }
    } catch { /* silent */ } finally {
      savingRef.current = false;
    }
  }

  async function reopenSigned() {
    if (studyId == null || status !== 'signed') return;
    if (!confirm(
      'Reopen this signed report and revert it to a draft?\n\n' +
      'The report status becomes editable again. This action is audited.'
    )) return;
    setSaving(true);
    setMessage('');
    try {
      const res = await apiCall('reports.reopen', { studyId });
      if (res.s !== 'ok') {
        setMessage('Reopen failed: ' + (res.error || ''));
        return;
      }
      signedRef.current = false;
      setStatus('draft');
      broadcastReportStatus('draft');
      showMessage('Report reopened as draft');
    } finally {
      setSaving(false);
    }
  }

  async function deleteDraft() {
    if (studyId == null || status !== 'draft') return;
    if (!confirm('Delete this draft report? The report will be removed and cannot be recovered.')) {
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const res = await apiCall('reports.delete', { studyId });
      if (res.s !== 'ok') {
        setMessage('Delete failed: ' + (res.error || ''));
        return;
      }
      dirtyRef.current = false;
      signedRef.current = false;
      setStatus('new');
      broadcastReportStatus('none');
      showMessage('Draft deleted');
      // Reset the editor to a truly blank document. HeaderView above the
      // editor renders the header - editor stays body-only.
      try { editorRef.current?.documentEditor?.openBlank(); } catch { /* ignore */ }
    } finally {
      setSaving(false);
    }
  }

  async function saveReport(andSign: boolean) {
    if (studyId == null) return;
    setSaving(true);
    setMessage('');
    try {
      // Persist the FULL document (header baked in). On reload we open
      // it verbatim - no strip + re-prepend dance. This is deliberate:
      // the styleName sentinel we used to detect the header boundary was
      // silently dropped by Syncfusion during round-trip serialization,
      // making the strip step unreliable and the resulting composed SFDT
      // fragile. Cost: header-designer changes made after a report was
      // saved no longer retroactively apply to that report.
      const content = await saveContentAsBase64();
      if (!content) { setMessage('No content'); return; }

      const res = await apiCall('reports.save', { studyId, content, contentType: 'sfdt' });
      if (res.s !== 'ok') { setMessage('Save failed: ' + (res.error || '')); return; }

      dirtyRef.current = false;
      setLastSavedAt(new Date());

      if (andSign) {
        const signRes = await apiCall('reports.sign', { studyId });
        if (signRes.s === 'ok') {
          setStatus('signed');
          signedRef.current = true;
          broadcastReportStatus('signed');
          showMessage('Report signed');
          // Notify runtime plugins subscribed to on_report_signed.
          // LIRA uses this to push the final report text back to Lenek +
          // flip the report status to SIGN_OFF for their audit trail.
          // Fire-and-forget — a plugin failure must not block the sign UX.
          if (studyId != null) {
            try {
              const plainText = extractPlainTextForPlugins(content);
              void notifyReportSigned(String(studyId), plainText);
            } catch { /* swallow */ }
          }
        } else {
          setMessage('Sign failed: ' + (signRes.error || ''));
        }
      } else {
        setStatus('draft');
        broadcastReportStatus('draft');
        showMessage('Draft saved');
      }
    } finally {
      setSaving(false);
    }
  }

  async function saveTemplateContent() {
    if (!templateMeta && !currentTemplateId) return;
    // Enforce meta fields at save time. The template editor page shows
    // these validation errors inline in its form, but this is the
    // authoritative check right before we ship the payload to the
    // backend (which currently doesn't enforce them).
    const nameTrim = (templateMeta?.name || '').trim();
    const modalityTrim = (templateMeta?.modality || '').trim();
    if (!nameTrim) {
      setMessage('Template name is required. Fill it in the form above.');
      return;
    }
    if (!modalityTrim) {
      setMessage('Modality is required. Fill it in the form above.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      // Uniqueness check: backend allows duplicate names, so enforce here.
      // Case-insensitive so "Chest CT" and "chest ct" collide.
      const listRes = await apiCall<TemplateMeta>('templates.list', {});
      const existing = (listRes.s === 'ok' ? listRes.data || [] : [])
        .filter((t) => t.templateId !== currentTemplateId)
        .map((t) => (t.name || '').trim().toLowerCase());
      if (existing.includes(nameTrim.toLowerCase())) {
        setMessage(`A template named "${nameTrim}" already exists. Pick a unique name.`);
        return;
      }

      // Templates must never carry a study-resolved header - if this edit
      // started from a report with a baked-in header, strip it here so the
      // template body is study-agnostic.
      const content = await saveContentAsBase64({ stripHeader: true });
      if (!content) { setMessage('No content'); return; }

      const res = await apiCall<{ templateId: number }>('templates.save', {
        templateId: currentTemplateId ?? 0,
        name: templateMeta?.name ?? '',
        modality: templateMeta?.modality ?? '',
        bodyPart: templateMeta?.bodyPart ?? '',
        content, contentType: 'sfdt',
      });
      if (res.s !== 'ok') { setMessage('Save failed: ' + (res.error || '')); return; }

      const newId = res.data?.[0]?.templateId ?? currentTemplateId;
      if (newId && newId !== currentTemplateId) setCurrentTemplateId(newId);
      dirtyRef.current = false;
      setLastSavedAt(new Date());
      showMessage('Template saved');
    } finally {
      setSaving(false);
    }
  }

  // Save-as-Template from within the report editor was removed in
  // cs3d5.54. Templates are now created / edited only from Settings >
  // Templates or the Template Manager dialog, so template content is
  // never contaminated with a specific patient's data by accident.

  function showMessage(msg: string) {
    setMessage(msg);
    setTimeout(() => setMessage(''), 2500);
  }

  async function refreshTemplates() {
    const list = await apiCall<TemplateMeta>('templates.list', { modality: modality || '' });
    if (list.s === 'ok') setTemplates(list.data || []);
  }

  /**
   * Length of the interim transcript we currently have inserted at the
   * cursor, if any. Deepgram sends an interim every ~100 ms as it refines
   * its guess; we keep the previous interim's length so we can erase it
   * before inserting the next one. This gives Zoom/Meet-style
   * "words appear as you speak" instead of waiting for the final.
   *
   * Zero means no interim is currently showing (either we just committed
   * a final, or dictation hasn't started this utterance yet).
   */
  const interimLenRef = useRef(0);

  /**
   * Erase the last `n` characters at the editor cursor. Uses Syncfusion's
   * handleBackKey (same primitive the macro-expansion path uses — see the
   * macro trigger erasure in the keypress handler) so we're guaranteed
   * the cursor stays in the same spot and no selection is left behind.
   */
  function eraseLastChars(editor: any, n: number) {
    for (let i = 0; i < n; i++) {
      try { editor.editor.handleBackKey(); } catch { /* ignore */ }
    }
  }

  /**
   * Insert dictated text at the editor cursor.
   *
   * Interim transcripts (isFinal=false) are inserted LIVE so the user sees
   * their words appear as they speak. Each new interim erases the previous
   * one before inserting; Deepgram's own server-side punctuation
   * substitutions already apply, so the interim text is clean enough to
   * read in real time.
   *
   * Final transcripts (isFinal=true) erase the last interim, then insert
   * the committed text with:
   *   - client-side applyReplacements for user shortcuts (NAD → "no acute
   *     distress", "new paragraph" → "\n\n", etc.)
   *   - macro voice-alias lookup (so saying a template's voice trigger
   *     expands to its full macro body)
   *   - \n → onEnter() so embedded newlines become real paragraph breaks
   *     in the Syncfusion editor (insertText('\n') doesn't do that
   *     reliably on its own).
   */
  function handleTranscript(text: string, isFinal: boolean) {
    if (!text) return;
    const editor = editorRef.current?.documentEditor;
    if (!editor) {
      console.warn('[ReportEditor] transcript dropped: no editor ref');
      return;
    }

    if (!isFinal) {
      // Interim transcripts are NOT inserted into the editor. The previous
      // approach (insert interim, erase+reinsert on each tick, commit on
      // final) looked slick but was flaky: Syncfusion's handleBackKey
      // isn't a clean character-deletion in all contexts — it can couple
      // with auto-correct, over/under-delete after commas, and silently
      // corrupt the inserted punctuation. Users saw commas disappear
      // intermittently as a result.
      //
      // Live feedback stays in the status bar (setMessage below), which
      // the user can see without the editor churning. On `isFinal` we
      // insert the committed transcript once, cleanly.
      setMessage(`“${text}”`);
      return;
    }

    // Final: commit the transcript. No interim to erase — nothing was
    // inserted during the interim phase.
    setMessage('');

    const macro = findMacroByVoice(macrosRef.current, text);
    const toInsert = macro ? macro.expansion : text;
    try {
      // Split on \n so each run inserts cleanly and every newline becomes a
      // real paragraph break (Syncfusion's insertText does NOT reliably
      // treat embedded \n as a line break — onEnter is the right primitive
      // to simulate the user pressing Return at the cursor).
      const parts = (' ' + toInsert).split('\n');
      for (let i = 0; i < parts.length; i++) {
        if (i > 0) editor.editor.onEnter();
        if (parts[i].length > 0) editor.editor.insertText(parts[i]);
      }
    } catch (err) {
      console.error('[ReportEditor] insertText failed', err);
      setMessage('Could not insert transcript into editor.');
      return;
    }
    if (macro) showMessage(`Macro: \\${macro.trigger}`);
    dirtyRef.current = true;
  }

  /** Fetch a URL and return a data: URL string suitable for Syncfusion's insertImage. */
  async function urlToDataUrl(url: string): Promise<string | null> {
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const blob = await res.blob();
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject();
        reader.readAsDataURL(blob);
      });
    } catch { return null; }
  }

  /** Insert the study's key images (heading + thumbnail + caption for each) at
   *  the current cursor position. No-op if this is a template or no studyId. */
  async function handleInsertKeyImages() {
    if (isTemplateMode || studyId == null) return;
    const editor = editorRef.current?.documentEditor;
    if (!editor) return;
    const items = await listKeyImages(studyId);
    if (items.length === 0) {
      showMessage('No key images flagged for this study.');
      return;
    }
    const acctoken = useAuthStore.getState().acctoken;
    const serverUrl = getServerUrl();
    // Insert a heading line + each image with caption underneath.
    editor.editor.insertText('\nKey Images\n');
    for (const k of items) {
      const url = keyImageThumbUrl(serverUrl, k.seriesId, k.imageIndex, acctoken);
      const dataUrl = await urlToDataUrl(url);
      if (dataUrl) {
        try { editor.editor.insertImage(dataUrl, 180, 180); } catch { /* fall back to text */ }
      }
      const caption = `  Series ${k.seriesId}, slice ${k.imageIndex + 1}${k.note ? ` — ${k.note}` : ''}\n`;
      editor.editor.insertText(caption);
    }
    dirtyRef.current = true;
    showMessage(`Inserted ${items.length} key image${items.length === 1 ? '' : 's'}.`);
  }

  const lastSavedText = lastSavedAt
    ? `${isTemplateMode ? 'Saved' : 'Autosaved'} ${lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : '';

  return (
    <div className={`${styles.panel} ${fullScreen ? styles.fullScreen : ''}`}>
      <div className={`${styles.header} ${isTemplateMode ? styles.headerTemplate : ''}`}>
        {onBack && (
          <button
            className={styles.secondaryBtn}
            style={{ marginRight: 8 }}
            onClick={() => {
              if (!dirtyRef.current || signedRef.current || confirmDiscardIfDirty('leave')) {
                onBack();
              }
            }}
            title={backLabel || 'Back'}
          >
            ← {backLabel || 'Back'}
          </button>
        )}
        <div className={styles.title}>
          {isTemplateMode ? (
            <>Template{templateMeta?.name ? `: ${templateMeta.name}` : ''}</>
          ) : (
            <>Report <span className={`${styles.status} ${styles['status_' + status]}`}>{status}</span></>
          )}
          {lastSavedText && <span className={styles.savedHint}>· {lastSavedText}</span>}
        </div>
        <div className={styles.actions}>
          {!isTemplateMode && (
            <select
              className={styles.tplSelect}
              value={tplDropdownValue}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '__manage__') {
                  // "Manage" is a command, not a state. Open the manager
                  // and revert the dropdown to whatever was selected before.
                  setShowManageTpl(true);
                  // Force a re-render so the DOM select syncs back to the
                  // controlled `tplDropdownValue` (which we didn't change).
                  setTplDropdownValue((prev) => prev);
                  return;
                }
                // Persist the selection so the user sees which template is
                // currently loaded. This is the visual confirmation they
                // were missing when we reset to '' after every pick.
                setTplDropdownValue(val);
                if (val) applyTemplate(Number(val));
              }}
              disabled={status === 'signed'}
            >
              <option value="">
                {templates.length === 0 ? 'No templates — manage' : 'Insert template…'}
              </option>
              {templates.map((t) => (
                <option key={t.templateId} value={String(t.templateId)}>
                  {t.name}{t.modality ? ` (${t.modality})` : ''}
                </option>
              ))}
              <option disabled>──────────</option>
              <option value="__manage__">Open Template Manager…</option>
            </select>
          )}

          {!isTemplateMode && (
            <DictateButton
              ref={dictateRef}
              onTranscript={handleTranscript}
              disabled={status === 'signed'}
            />
          )}

          {isTemplateMode ? (
            <button
              className={styles.saveBtn}
              onClick={saveTemplateContent}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save Template'}
            </button>
          ) : (
            <>
              <button
                className={styles.secondaryBtn}
                onClick={handleInsertKeyImages}
                disabled={saving || status === 'signed'}
                title="Insert all flagged key images for this study at the cursor"
              >
                Insert Key Images
              </button>
              {status === 'draft' && (
                <button
                  className={styles.dangerBtn}
                  onClick={deleteDraft}
                  disabled={saving}
                  title="Delete this draft and start over"
                >
                  Delete Draft
                </button>
              )}
              {status === 'signed' && isAdminUsername(username) && (
                <button
                  className={styles.dangerBtn}
                  onClick={reopenSigned}
                  disabled={saving}
                  title="Reopen this signed report as a draft (admin only, audited)"
                >
                  Reopen as Draft
                </button>
              )}
              <button
                className={styles.saveBtn}
                onClick={() => saveReport(false)}
                disabled={saving || status === 'signed'}
              >
                {saving ? 'Saving…' : 'Save Draft'}
              </button>
              <button
                className={styles.signBtn}
                onClick={() => saveReport(true)}
                disabled={saving || status === 'signed'}
              >
                Sign &amp; Save
              </button>
            </>
          )}
          {onClose && (
            <button
              className={styles.closeBtn}
              onClick={() => {
                if (confirmDiscardIfDirty('close the editor')) onClose();
              }}
            >×</button>
          )}
        </div>
      </div>
      {/* Always-present status strip. Reserving fixed vertical space
          means the editor doesn't get pushed down / scroll when interim
          dictation transcripts appear and disappear - a common cause
          of the mid-dictation editor-jump the user reported. */}
      <div className={message ? styles.message : styles.messageEmpty}>{message}</div>
      {/* Header lives INSIDE the editor document (inserted on load via
          installHeader / installHeaderPrependingBody), so the report
          visually looks the way it'll actually print. The header region
          is refreshed from the current HeaderConfig on every load, and
          stripped from the SFDT on save - so anything the user might type
          into it doesn't persist. Templates render with placeholder tokens
          instead of real study data. */}
      <div className={styles.editorWrap}>
        <DocumentEditorContainerComponent
          ref={editorRef}
          height="100%"
          enableToolbar
          serviceUrl={getDocxServiceUrl()}
          // Syncfusion's DocumentEditor prints by rasterizing each page from
          // its render canvas to a PNG and printing that. Default DPR of 1
          // gives ~96 DPI output that looks fuzzy vs MS Word. Bumping to 3
          // renders the canvas 3x larger before rasterization; the printer
          // driver downsamples from a much sharper source. Still raster -
          // for true vector-quality print use the "Print (PDF)" toolbar item
          // which routes through the dcx-service DocIORenderer path.
          // Reference: Syncfusion forum 163880, help.syncfusion.com/document-processing/word/word-processor/react/print
          documentEditorSettings={{ printDevicePixelRatio: 3 }}

          toolbarItems={[
            'Undo', 'Redo', 'Separator',
            'Image', 'Table', 'Hyperlink', 'Separator',
            'Header', 'Footer', 'PageSetup', 'PageNumber', 'Break', 'Separator',
            'Find', 'Separator',
            'Comments', 'Separator',
            // Quality-of-life: after the user tweaks Page Setup on the
            // current report, one click makes that geometry the default
            // for every future new report + template load. Writes the
            // current section format to user_settings.reportPageFormat.
            {
              prefixIcon: 'e-icons e-page-setup',
              tooltipText: 'Save the current Page Setup as the default for future reports',
              text: 'Save as Default',
              id: 'save-page-default',
            } as any,
            'Separator',
            // Two print modes side-by-side. Users pick per their need:
            //   - Quick: instant, canvas-based, printDevicePixelRatio=3 for
            //     legibility. Fuzzy under a magnifier but fine for chart work.
            //   - PDF: server round-trip via dcx-service /ExportPdf, opens a
            //     vector PDF in a new tab. Print from there for MS-Word-
            //     equivalent quality. Adds a couple of seconds.
            {
              prefixIcon: 'e-icons e-print',
              tooltipText: 'Print at screen resolution (fast, raster)',
              text: 'Print (Quick)',
              id: 'custom-print',
            } as any,
            {
              prefixIcon: 'e-icons e-export-pdf',
              tooltipText: 'Convert to PDF on the server for MS-Word-equivalent print quality (slower)',
              text: 'Print (PDF)',
              id: 'custom-print-pdf',
            } as any,
          ]}
          toolbarClick={(args: any) => {
            if (args?.item?.id === 'custom-print') {
              const ed: any = editorRef.current?.documentEditor;
              try { ed?.print(); } catch (err) { console.error('Print failed', err); }
            } else if (args?.item?.id === 'custom-print-pdf') {
              void printAsPdf();
            } else if (args?.item?.id === 'save-page-default') {
              void saveCurrentPageAsDefault();
            }
          }}
        />
      </div>

      {showManageTpl && (
        <ManageTemplatesDialog
          onClose={() => setShowManageTpl(false)}
          onChanged={refreshTemplates}
        />
      )}
    </div>
  );
}
